// `FilterBar` (rev-7, D-426) — one row of filters for a list. The audit found
// eleven always-visible filters on the waste ledger, nine on movements and
// nine full-width controls stacked above the first row of work orders on a
// phone. The bar keeps three things in view and puts the rest one press away:
//
//   * a search box, when the list has one;
//   * at most two inline filters, the ones a reader changes most;
//   * a "Filters (n)" button whose panel holds every other filter — a popover
//     below the button from 769 px up, a sheet at the bottom of the glass
//     below that — and, on a phone, the inline filters too, so the first
//     record of the list is on the first screen.
//
// Applied filters show as removable chips under the row, so a reader can see
// and undo what narrowed the list without opening the panel. An "inline"
// filter that holds a value is a chip only on a phone, where it is hidden.
//
// i18n-free like the other primitives: every word arrives from the caller.
// The controls are the caller's own `Field`s and `Select`s, so their test ids
// and their state stay where they are; the bar only decides where each one is
// drawn, and draws each exactly once.

import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { IconSearch } from "./icons";
import { useDesktop } from "./use-desktop";

export interface FilterChip {
  key: string;
  /** "Status: Open" — the filter and its value, in the caller's words. */
  label: string;
  onRemove: () => void;
}

export interface FilterBarSearch {
  value: string;
  onChange: (value: string) => void;
  /** The box's accessible name. */
  label: string;
  placeholder?: string | undefined;
  /** The longest text the box takes, when the list's address caps its search. */
  maxLength?: number | undefined;
  "data-testid"?: string | undefined;
}

/**
 * The list's filters and the words for their panel. The words are required
 * whenever any filter is: on a phone the inline filters move into the panel
 * too, so a list with only inline filters still has a button and a panel that
 * need names.
 */
export interface FilterBarFilters {
  /** The one or two filters a reader changes most, drawn in the row. */
  inline?: ReactNode;
  /** Every other filter, behind the button. */
  more?: ReactNode;
  /** The button's name, "Filters (2)": the caller counts the applied filters. */
  label: string;
  /** The panel's heading and accessible name, e.g. "Filters". */
  title: string;
  /** The closing button's word, e.g. "Done". */
  doneLabel: string;
}

export interface FilterBarProps {
  search?: FilterBarSearch | undefined;
  /** Omit when the list has no filters besides its search. */
  filters?: FilterBarFilters | undefined;
  /** Applied filters, removable one at a time. */
  chips?: readonly FilterChip[] | undefined;
  /** "Remove Status: Open" — the accessible name of a chip's remove button. */
  removeLabel?: ((chip: FilterChip) => string) | undefined;
  /** Clears every filter at once; shown when two or more chips are applied. */
  clearAll?: { label: string; onClear: () => void } | undefined;
  /** Right-side tools of the row (saved views, Columns, Export). */
  actions?: ReactNode;
  /**
   * Called whenever the panel closes: Done, Escape, a press outside or the
   * button. A box that applies on blur needs it, because a press outside
   * unmounts the focused box before the browser moves focus, so its blur
   * never runs and what was typed would wait unapplied.
   */
  onClose?: (() => void) | undefined;
  "data-testid"?: string;
}

export function FilterBar({
  search,
  filters,
  chips = [],
  removeLabel,
  clearAll,
  actions,
  onClose,
  ...rest
}: FilterBarProps) {
  const desktop = useDesktop();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement | null>(null);
  const button = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const testId = rest["data-testid"] ?? "filter-bar";
  // The latest onClose, so a close from the listeners below applies what the
  // caller holds now and not what it held when the panel opened.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const inline = filters?.inline;
  const more = filters?.more;
  const hasInline = inline !== undefined && inline !== null && inline !== false;
  const hasMore = more !== undefined && more !== null && more !== false;
  // On a phone the inline filters move into the sheet, so the sheet exists
  // whenever there is anything to put in it.
  const hasPanel = hasMore || (!desktop && hasInline);

  useEffect(() => {
    if (!open) return undefined;
    panel.current?.focus();
    const onPointer = (e: PointerEvent): void => {
      if (root.current !== null && !root.current.contains(e.target as Node)) {
        setOpen(false);
        onCloseRef.current?.();
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      // A control inside the panel that closed its own popover on this
      // Escape marks it handled; one press closes one layer.
      if (e.key === "Escape" && !e.defaultPrevented) {
        setOpen(false);
        button.current?.focus();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = (): void => {
    setOpen(false);
    button.current?.focus();
    onClose?.();
  };

  return (
    <div className="ds-fbar" ref={root} data-testid={testId}>
      <div className="ds-fbar-row">
        {search !== undefined ? (
          <label className="ds-fbar-search">
            <IconSearch />
            <input
              type="search"
              className="ds-input"
              value={search.value}
              aria-label={search.label}
              placeholder={search.placeholder}
              maxLength={search.maxLength}
              data-testid={search["data-testid"]}
              onChange={(e) => {
                search.onChange(e.target.value);
              }}
            />
          </label>
        ) : null}
        {desktop && hasInline ? <div className="ds-fbar-inline">{inline}</div> : null}
        {hasPanel ? (
          <button
            type="button"
            ref={button}
            className="ds-btn ds-fbar-more"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            data-testid={`${testId}-more`}
            onClick={() => {
              setOpen(!open);
              if (open) onClose?.();
            }}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M2.5 4h11M4.5 8h7M6.5 12h3" />
            </svg>
            {filters.label}
          </button>
        ) : null}
        {actions !== undefined ? <div className="ds-fbar-actions">{actions}</div> : null}
      </div>
      {open && hasPanel ? (
        <div
          ref={panel}
          id={panelId}
          role="dialog"
          aria-modal="false"
          aria-label={filters.title}
          tabIndex={-1}
          className={desktop ? "ds-fbar-panel" : "ds-fbar-panel ds-fbar-sheet"}
          data-testid={`${testId}-panel`}
        >
          <p className="ds-fbar-title">{filters.title}</p>
          <div className="ds-fbar-fields">
            {desktop ? null : inline}
            {more}
          </div>
          <div className="ds-fbar-foot">
            <button type="button" className="ds-btn ds-btn-primary" onClick={close}>
              {filters.doneLabel}
            </button>
          </div>
        </div>
      ) : null}
      {chips.length > 0 ? (
        <div className="ds-fbar-chips" data-testid={`${testId}-chips`}>
          {chips.map((chip) => (
            <span className="ds-fchip" key={chip.key}>
              <span className="ds-fchip-label">{chip.label}</span>
              <button
                type="button"
                className="ds-fchip-x"
                aria-label={removeLabel?.(chip) ?? chip.label}
                onClick={chip.onRemove}
              >
                <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" />
                </svg>
              </button>
            </span>
          ))}
          {clearAll !== undefined && chips.length > 1 ? (
            <button type="button" className="ds-lnk ds-fbar-clear" onClick={clearAll.onClear}>
              {clearAll.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
